import {
  canonicalizeToolGrassType,
  canonicalizeToolLawnSize,
  canonicalizeToolSunlight,
  normalizeGrassType,
} from '@/lib/lawn-plan-engine'

export interface SavedLawnLike {
  id: string
  name?: string | null
  zip_code?: string | null
  grass_type?: string | null
  lawn_size?: string | null
  sunlight?: string | null
  soil_type?: string | null
  created_at?: string | null
}

export interface ToolLawnPrefill {
  zipCode?: string
  grassType?: string
  lawnSize?: string
  lawnSizeSqft?: number
  soilType?: string
  sunlight?: string
}

export function isUnknownSavedGrass(grassType?: string | null): boolean {
  if (!grassType) return false
  const resolved = normalizeGrassType(grassType)
  return !resolved.isKnown
}

export function parseSavedLawnSize(lawnSize?: string | null): number | undefined {
  return canonicalizeToolLawnSize(lawnSize) ?? undefined
}

export function normalizeSavedSoilType(soilType?: string | null): string | undefined {
  if (!soilType) return undefined
  const normalized = soilType.trim().toLowerCase()
  const aliases: Record<string, string> = {
    sand: 'sand',
    sandy: 'sand',
    loam: 'loam',
    loamy: 'loam',
    clay: 'clay',
    clayey: 'clay',
    silt: 'silt',
    silty: 'silt',
  }
  return aliases[normalized]
}

export function chooseDefaultLawn<T extends SavedLawnLike>(
  lawns: T[],
  selectedLawnId?: string | null,
): T | null {
  if (lawns.length === 0) return null
  if (selectedLawnId) {
    const selected = lawns.find((lawn) => lawn.id === selectedLawnId)
    if (selected) return selected
  }
  return lawns[0] ?? null
}

export function buildToolLawnPrefill(lawn?: SavedLawnLike | null): ToolLawnPrefill {
  if (!lawn) return {}

  const zipCode = lawn.zip_code?.trim()
  const resolvedGrass = normalizeGrassType(lawn.grass_type)
  const grassType = resolvedGrass.isKnown
    ? canonicalizeToolGrassType(lawn.grass_type)
    : undefined
  const lawnSizeSqft = parseSavedLawnSize(lawn.lawn_size)
  const soilType = normalizeSavedSoilType(lawn.soil_type)
  const sunlight = canonicalizeToolSunlight(lawn.sunlight) ?? undefined

  return {
    zipCode: zipCode && /^\d{5}$/.test(zipCode) ? zipCode : undefined,
    grassType,
    lawnSize: lawn.lawn_size ?? undefined,
    lawnSizeSqft,
    soilType,
    sunlight,
  }
}
